'use client';

import React, { useEffect, useState } from 'react';
import ProductCard from './ProductCard';
import { Product } from '@/models/ProductTypes';

type ProductCardContainerProps = {
  id: string;
};

const ProductCardContainer: React.FC<ProductCardContainerProps> = ({ id }) => {
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchProduct = async () => {
      try {
        setLoading(true);
        setError(null);

        const res = await fetch('/api/store/product-list', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          // API expects { productList: string[] }
          body: JSON.stringify({ productList: [id] }),
          cache: 'no-store',
        });

        if (!res.ok) {
          const text = await res.text().catch(() => '');
          throw new Error(`Failed to fetch product (${res.status}): ${text}`);
        }

        const arr = (await res.json()) as Product[] | unknown;
        const first = Array.isArray(arr) && arr.length > 0 && typeof arr[0] === 'object' ? (arr[0] as Product) : null;

        if (!cancelled) setProduct(first);
      } catch (err) {
        console.error(err);
        if (!cancelled) setError('Villa við að sækja vöru');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchProduct();
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (loading) {
    return (
      <div className="flex h-[200px] items-center justify-center rounded bg-white-50 shadow-sm">
        <p>Hleð vörunni...</p>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="flex h-[200px] items-center justify-center rounded bg-white-50 shadow-sm">
        <p>{error ?? 'Fann ekki vöru'}</p>
      </div>
    );
  }

  return (
    <ProductCard
      key={id}
      productId={id}
      name={product.productName || 'Óþekkt vara'}
      group={product.productGroup || ''}
      category={product.productCategory || ''}
      price={product.productPrice ?? 0}
      available={true}
    />
  );
};

export default ProductCardContainer;
